import type { Player, Spell, SpellConfig, SpellOffer } from "./types";
import type { PurchaseStatus } from "./spells";

/** Only copy and units live here. All current/next values come from the server. */
const BENEFIT: Record<
  Spell,
  { label: string; key: keyof SpellConfig; unit: string }
> = {
  fireball: { label: "Push", key: "push", unit: "m/s" },
  dash: { label: "Range", key: "range", unit: "m" },
  teleport: { label: "Range", key: "range", unit: "m" },
  shield: { label: "Protection", key: "duration_seconds", unit: "s" },
  meteor: { label: "Impact radius", key: "radius", unit: "m" },
  dome: { label: "Field duration", key: "duration_seconds", unit: "s" },
  lightning: { label: "Cooldown", key: "cooldown_seconds", unit: "s" },
};
export function spellRank(player: Player | undefined, spell: Spell): number {
  const slot = player?.loadout.indexOf(spell) ?? -1;
  return slot < 0 ? 0 : (player?.upgrade_ranks?.[slot] ?? 0);
}
export function upgradeStatus(
  offer: SpellOffer,
  player: Player | undefined,
  round: number,
  pending: boolean,
): PurchaseStatus {
  if (!player) return { label: "Observers cannot upgrade", disabled: true };
  if (!player.loadout.includes(offer.spell))
    return { label: "Learn this spell first", disabled: true };
  const rank = spellRank(player, offer.spell);
  const next = offer.upgrades?.find((upgrade) => upgrade.rank === rank + 1);
  if (!next)
    return {
      label: offer.upgrades?.length ? "Fully upgraded" : "Learned",
      disabled: true,
    };
  if (pending) return { label: "Confirming…", disabled: true };
  if (player.ready) return { label: "Build is ready", disabled: true };
  if (round < next.unlockRound)
    return { label: `Opens round ${next.unlockRound}`, disabled: true };
  if (player.gold < next.price)
    return {
      label: `Need ${next.price - player.gold} more gold`,
      disabled: true,
    };
  return {
    label: `Upgrade ${next.rank === 1 ? "I" : "II"} · ${next.price} gold`,
    disabled: false,
  };
}
export function upgradeDetail(
  offer: SpellOffer,
  player: Player | undefined,
): string {
  const rank = spellRank(player, offer.spell);
  const current =
    rank === 0
      ? offer.config
      : offer.upgrades?.find((u) => u.rank === rank)?.config;
  const next = offer.upgrades?.find((u) => u.rank === rank + 1)?.config;
  if (!current) return "";
  const { label, key, unit } = BENEFIT[offer.spell];
  const format = (config: SpellConfig) =>
    `${Number(config[key].toFixed(2))}${unit === "m/s" ? " " : ""}${unit}`;
  return `${label} ${format(current)}${next ? ` → ${format(next)}` : ""}`;
}
export function upgradeApplied(
  player: Player | undefined,
  spell: Spell,
  previousRank: number,
): boolean {
  return (
    !!player?.loadout.includes(spell) && spellRank(player, spell) > previousRank
  );
}
